import type { SitePage, SiteSection } from '../domains/site/schema';

type SectionPromptMatch = {
  sectionId: string;
  score: number;
};

export type SectionPromptCandidate = {
  text: string;
  weight: number;
};

export function normalizePromptText(value: string) {
  return value
    .toLowerCase()
    .replace(/[\s\u3000"'`“”‘’「」『』（）()［］【】{}<>〈〉《》、。,.!！?？:：;；・/\\_-]+/gu, '');
}

export function collectSectionPromptCandidates(section: SiteSection): SectionPromptCandidate[] {
  const candidates: SectionPromptCandidate[] = [];

  const pushCandidate = (text: unknown, weight: number) => {
    if (typeof text !== 'string') return;
    const trimmed = text.trim();
    if (trimmed.length < 2) return;
    candidates.push({ text: trimmed, weight });
  };

  if ('title' in section) {
    pushCandidate(section.title, 50);
  }
  if ('headline' in section) {
    pushCandidate(section.headline, 48);
  }
  if ('eyebrow' in section) {
    pushCandidate(section.eyebrow, 24);
  }

  if ('items' in section && Array.isArray(section.items)) {
    for (const item of section.items) {
      if (!item || typeof item !== 'object') continue;
      pushCandidate('title' in item ? item.title : undefined, 90);
      pushCandidate('label' in item ? item.label : undefined, 88);
      pushCandidate('question' in item ? item.question : undefined, 86);
      pushCandidate('name' in item ? item.name : undefined, 84);
      pushCandidate('price' in item ? item.price : undefined, 40);
      pushCandidate('value' in item ? item.value : undefined, 36);
    }
  }

  return candidates;
}

export function inferSectionIdFromPrompt(page: SitePage | null, prompt: string): string | null {
  if (!page) return null;

  const normalizedPrompt = normalizePromptText(prompt);
  if (normalizedPrompt.length < 2) return null;

  let bestMatch: SectionPromptMatch | null = null;

  for (const section of page.sections) {
    const candidates = collectSectionPromptCandidates(section);

    for (const candidate of candidates) {
      const normalizedCandidate = normalizePromptText(candidate.text);
      if (normalizedCandidate.length < 2) continue;
      if (!normalizedPrompt.includes(normalizedCandidate)) continue;

      const score = candidate.weight + normalizedCandidate.length;
      if (!bestMatch || score > bestMatch.score) {
        bestMatch = {
          sectionId: section.id,
          score,
        };
      }
    }
  }

  return bestMatch?.sectionId ?? null;
}
